import { withErrorCode } from '@/lib/errors'
import { Link } from 'react-router-dom'
import type { UseFormRegister, FieldErrors } from 'react-hook-form'
import { HoldConfirmButton } from '@/components/ui/hold-confirm-button'
import { MagicTextarea as Textarea } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import { MemberSearchPicker } from '@/components/common/MemberSearchPicker'
import type { SubmissionFormValues } from '../types'
import type { ChoreographerRef, CompanyMemberDocument } from '@/types'

interface SubmissionSectionProps {
  register: UseFormRegister<SubmissionFormValues>
  errors: FieldErrors<SubmissionFormValues>
  isSubmitting: boolean
  submitError: string | null
  formattedTotal: string
  selectedProviders: ChoreographerRef[]
  onProvidersChange: (providers: ChoreographerRef[]) => void
  hireableMembers: Array<CompanyMemberDocument & { id: string }>
}

export function SubmissionSection({
  register,
  errors,
  isSubmitting,
  submitError,
  formattedTotal,
  selectedProviders,
  onProvidersChange,
  hireableMembers,
}: SubmissionSectionProps) {
  return (
    <section className="space-y-6">
      <h2 className="text-xl font-semibold">Finalize Your Request</h2>

      <div className="rounded-xl border bg-muted/30 p-5 space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="additionalInfo">Additional relevant information</Label>
          <Textarea
            id="additionalInfo"
            rows={3}
            placeholder="Anything else we should know?"
            {...register('additionalInfo')}
          />
        </div>

        <MemberSearchPicker
          label="Requested providers (optional)"
          members={hireableMembers}
          selected={selectedProviders}
          onChange={onProvidersChange}
        />

        {/* Mailing list */}
        <div className="space-y-2">
          <Label>Would you like to join our mailing list to receive offers and promotions?</Label>
          <div className="flex items-center gap-6 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                value="yes"
                {...register('mailingOptIn')}
                className="accent-primary"
              />
              Yes
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                value="no"
                {...register('mailingOptIn')}
                className="accent-primary"
              />
              No
            </label>
          </div>
        </div>

        {/* ToS */}
        <div className="space-y-1">
          <div className="flex items-start gap-2.5">
            <input
              id="tosAccepted"
              type="checkbox"
              aria-invalid={!!errors.tosAccepted}
              {...register('tosAccepted')}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <Label htmlFor="tosAccepted" className="cursor-pointer font-normal leading-normal">
              I have read and agree to the{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Terms of Service
              </Link>{' '}
              *
            </Label>
          </div>
          {errors.tosAccepted && (
            <p className="ml-6.5 text-xs text-destructive">{withErrorCode(String(errors.tosAccepted.message), 'app/validation')}</p>
          )}
        </div>

        {/* Booking acknowledgment */}
        <div className="space-y-1">
          <div className="flex items-start gap-2.5">
            <input
              id="bookingAcknowledge"
              type="checkbox"
              aria-invalid={!!errors.bookingAcknowledge}
              {...register('bookingAcknowledge')}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <Label htmlFor="bookingAcknowledge" className="cursor-pointer font-normal leading-normal">
              I understand that submitting this form does not constitute confirmation of booking *
            </Label>
          </div>
          {errors.bookingAcknowledge && (
            <p className="ml-6.5 text-xs text-destructive">{withErrorCode(String(errors.bookingAcknowledge.message), 'app/validation')}</p>
          )}
        </div>

        {submitError && (
          <p className="text-sm text-destructive">{submitError}</p>
        )}

        {/* Submit */}
        <div className="flex flex-col items-start gap-2 pt-1">
          {formattedTotal !== '$0.00' && (
            <p className="text-sm text-muted-foreground">
              Estimated total:{' '}
              <span className="font-semibold text-foreground">{formattedTotal}</span>
            </p>
          )}
          <HoldConfirmButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit Request'}
          </HoldConfirmButton>
        </div>
      </div>
    </section>
  )
}
